import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ConditionalWrapper } from '@qpr/components';
import React from 'react';
import { Link } from 'react-router-dom';
import { checkIfExternal } from '../../../utils/url-utils';

const PREFIX = 'LandingPageCard';

const classes = {
  linkStyle: `${PREFIX}-linkStyle`,
  svgCircle: `${PREFIX}-svgCircle`,
  svgText: `${PREFIX}-svgText`
};

const StyledLink = styled('div')(({ theme }) => ({
  [`& .${classes.linkStyle}`]: {
    textDecoration: 'none',
    color: theme.palette.text.primary
  }
}));

const StyledSvg = styled('svg')(({ theme }) => ({
  [`& .${classes.svgCircle}`]: {
    stroke: '#A2C7DD',
    strokeWidth: '6',
    fill: 'white'
  },

  [`& .${classes.svgText}`]: {
    fill: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '20px',
    textAnchor: 'middle'
  }
}));

export interface LandingCardProps {
  name: string;
  link: string;
  image: string;
  orderingNumber?: number;
  first?: boolean;
  last?: boolean;
  enabled: boolean;
  tenant?: string;
}

const LandingPageCard = ({
  name,
  link,
  image,
  orderingNumber,
  first,
  last,
  enabled,
  tenant = ''
}: LandingCardProps) => {
  const printOrderingNumber = (
    idx: number | undefined,
    isfirst: boolean | undefined,
    islast: boolean | undefined
  ): JSX.Element => {
    return idx !== undefined ? (
      <StyledSvg height="56" width="100%">
        <g className={classes.svgCircle}>
          {!isfirst && <line x1="0" y1="50%" x2="50%" y2="50%" className={classes.svgCircle} />}
          {!islast && <line x1="50%" y1="50%" x2="100%" y2="50%" strokeWidth="6" />}
          <circle cx="50%" cy="50%" r="20" />
        </g>
        <text dy="6" x="50%" y="50%" className={classes.svgText}>
          {idx}
        </text>
      </StyledSvg>
    ) : (
      <> </>
    );
  };
  const isActive = link && enabled;

  return (
    <ConditionalWrapper
      condition={checkIfExternal(link)}
      wrapper={(children: React.ReactNode) =>
        isActive ? (
          <StyledLink>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={link}
              className={classes.linkStyle}
              data-testid="card-link-external"
            >
              {children}
            </a>
          </StyledLink>
        ) : (
          <>{children}</>
        )
      }
      wrapperFalse={(children: React.ReactNode) =>
        isActive ? (
          <StyledLink>
            <Link
              to={`/${tenant}${link}`}
              className={classes.linkStyle}
              data-testid="card-link-internal"
            >
              {children}
            </Link>
          </StyledLink>
        ) : (
          <>{children}</>
        )
      }
    >
      <Card
        data-testid="card-root"
        sx={{
          margin: 0,
          width: '100%',
          height: '325px',
          display: 'block',
          backgroundColor: 'transparent',
          color: 'white',
          boxShadow: 'none',
          '&:hover': isActive
            ? {
                fontSize: '48px',
                '& $avatarDiv': {
                  transform: 'scale(1.1)'
                },
                '& $svgCircle': {
                  fill: '#21B6FF',
                  stroke: '#21B6FF'
                },
                '& $svgText': {
                  fill: 'white'
                },
                '& $cardTitle': {
                  fontSize: '24px',
                  letterSpacing: '0‬rem',
                  fontWeight: 400
                }
              }
            : {}
        }}
      >
        <Box data-testid="avatar-div" sx={{ padding: 1, alignContent: 'center', height: '150px' }}>
          <CardMedia
            image={image}
            data-testid="avatar-image"
            sx={{ marginTop: '40px', height: '100px', backgroundSize: 'contain' }}
          />
        </Box>
        <CardContent
          sx={{ textAlign: 'center', paddingLeft: '0px', paddingRight: '0px', paddingTop: '40px' }}
        >
          {printOrderingNumber(orderingNumber, first, last)}
          <Typography variant="h6" data-testid="card-title">
            {name}
          </Typography>
        </CardContent>
      </Card>
    </ConditionalWrapper>
  );
};

export default LandingPageCard;
